import {
  parseChart,
  parseChartSpans,
  type ChartLine,
  type ChartSegment,
  type ChartToken,
} from './chart.ts'
import { parseKey, transposeChord, type Key } from './music.ts'

/**
 * Editing a chart one line at a time — how the phone editor works, where a
 * whole chart in a text box is miserable to edit.
 *
 * The source text stays the truth. A chart is read as blocks (each parsed
 * line with the source lines it came from), an edit replaces one block's
 * lines with the edited line written out again, and every other line of the
 * source is kept exactly as it was — tabs, spacing, format and all. A line
 * that is edited is written in one consistent format: ChordPro inline chords
 * (`[G]Amazing [C]grace`), Planning Center codes for breaks and key changes.
 */

// ---- blocks ----------------------------------------------------------------------------

export interface ChartBlock {
  /** Its place among the chart's blocks. */
  index: number
  /** The source lines it stands for: `start` to `end`, exclusive. */
  start: number
  end: number
  /** What it reads as; null for a source line that shows nothing (`{title: …}`, `{end_of_verse}`). */
  line: ChartLine | null
}

const linesOf = (source: string) => source.replace(/\r\n?/g, '\n').split('\n')

/** A chart's blocks, in source order: every source line is in at least one. */
export function chartBlocks(source: string): ChartBlock[] {
  const { chart, spans } = parseChartSpans(source)
  const covered = new Set<number>()
  const blocks: Omit<ChartBlock, 'index'>[] = chart.lines.map((line, i) => {
    const span = spans[i] ?? { start: 0, end: 0 }
    for (let at = span.start; at < span.end; at += 1) covered.add(at)
    return { start: span.start, end: span.end, line }
  })
  linesOf(source).forEach((_, at) => {
    if (!covered.has(at)) blocks.push({ start: at, end: at + 1, line: null })
  })
  // Stable: a heading and the lyric it shares a line with keep their order.
  return blocks
    .map((block, i) => ({ block, i }))
    .sort((a, b) => a.block.start - b.block.start || a.i - b.i)
    .map(({ block }, index) => ({ ...block, index }))
}

/**
 * Blocks that share source lines go together ("FINAL:  F#m" is a heading
 * and the chord of the lyric under it): the run of blocks around `index`
 * whose lines overlap, and the lines they cover.
 */
function clusterOf(blocks: ChartBlock[], index: number) {
  let first = index
  let last = index
  const at = (i: number) => blocks[i] ?? { start: 0, end: 0 }
  let end = at(index).end
  let start = at(index).start
  while (first > 0 && at(first - 1).end > start) {
    first -= 1
    start = Math.min(start, at(first).start)
  }
  while (last < blocks.length - 1 && at(last + 1).start < end) {
    last += 1
    end = Math.max(end, at(last).end)
  }
  return { first, last, start, end }
}

/** The source text a block stands for (with any block it shares lines with). */
export function blockSource(source: string, index: number): string {
  const blocks = chartBlocks(source)
  if (!blocks[index]) return ''
  const { start, end } = clusterOf(blocks, index)
  return linesOf(source).slice(start, end).join('\n')
}

function splice(source: string, start: number, end: number, replacement: string[]): string {
  const lines = linesOf(source)
  lines.splice(start, end - start, ...replacement)
  return lines.join('\n')
}

/**
 * A block replaced by an edited line (null: removed). The lines around it
 * are untouched; a block sharing its lines is written out with it.
 */
export function replaceBlock(source: string, index: number, line: ChartLine | null): string {
  const blocks = chartBlocks(source)
  if (!blocks[index]) return source
  const cluster = clusterOf(blocks, index)
  if (cluster.first === cluster.last)
    return splice(source, cluster.start, cluster.end, line ? [serializeLine(line)] : [])
  const written: string[] = []
  for (let i = cluster.first; i <= cluster.last; i += 1) {
    const next = i === index ? line : (blocks[i]?.line ?? null)
    if (next) written.push(serializeLine(next))
  }
  return splice(source, cluster.start, cluster.end, written)
}

/** A block's source replaced by text typed as is (the "edit as text" escape hatch). */
export function replaceBlockSource(source: string, index: number, text: string): string {
  const blocks = chartBlocks(source)
  if (!blocks[index]) return source
  const { start, end } = clusterOf(blocks, index)
  return splice(source, start, end, linesOf(text))
}

/** Lines inserted after a block (`-1`: at the top). */
export function insertAfter(source: string, index: number, lines: ChartLine[]): string {
  const written = lines.map(serializeLine)
  if (index < 0) return splice(source, 0, 0, written)
  const blocks = chartBlocks(source)
  if (!blocks[index]) return splice(source, linesOf(source).length, linesOf(source).length, written)
  const { end } = clusterOf(blocks, index)
  return splice(source, end, end, written)
}

/** Swaps two neighbouring runs of source lines. */
function swap(
  source: string,
  a: { start: number; end: number },
  b: { start: number; end: number },
) {
  const lines = linesOf(source)
  const first = lines.slice(a.start, a.end)
  const second = lines.slice(b.start, b.end)
  lines.splice(a.start, b.end - a.start, ...second, ...lines.slice(a.end, b.start), ...first)
  return lines.join('\n')
}

/** A block moved up (`-1`) or down (`+1`) past its neighbour. */
export function moveBlock(source: string, index: number, delta: -1 | 1): string {
  const blocks = chartBlocks(source)
  if (!blocks[index]) return source
  const here = clusterOf(blocks, index)
  const neighbourIndex = delta < 0 ? here.first - 1 : here.last + 1
  if (!blocks[neighbourIndex]) return source
  const there = clusterOf(blocks, neighbourIndex)
  return delta < 0 ? swap(source, there, here) : swap(source, here, there)
}

/** The source lines of the section a block starts: its heading up to the next heading. */
function sectionRange(blocks: ChartBlock[], index: number, total: number) {
  const start = blocks[index]?.start ?? 0
  const next = blocks.find((block) => block.index > index && block.line?.kind === 'section')
  return { start, end: next ? next.start : total }
}

/** A whole section (heading and everything under it) moved before or after its neighbour. */
export function moveSection(source: string, index: number, delta: -1 | 1): string {
  const blocks = chartBlocks(source)
  const total = linesOf(source).length
  if (blocks[index]?.line?.kind !== 'section') return source
  const here = sectionRange(blocks, index, total)
  if (delta < 0) {
    const previous = [...blocks]
      .reverse()
      .find((block) => block.index < index && block.line?.kind === 'section')
    if (!previous) return source
    return swap(source, sectionRange(blocks, previous.index, total), here)
  }
  const next = blocks.find((block) => block.index > index && block.line?.kind === 'section')
  if (!next) return source
  return swap(source, here, sectionRange(blocks, next.index, total))
}

/** The block index of each section heading, for jumping and moving whole sections. */
export function sectionStarts(source: string): number[] {
  return chartBlocks(source)
    .filter((block) => block.line?.kind === 'section')
    .map((block) => block.index)
}

// ---- writing a line out -----------------------------------------------------------------

type Flags = Pick<ChartSegment, 'bold' | 'italic' | 'underline' | 'note' | 'chartOnly'>

/** A lyric line as the editor holds it: the words, each letter's styling, and chords at offsets. */
export interface LineDraft {
  text: string
  flags: Flags[]
  /** In order of `at`; `at` is an offset into `text` (at most its length). */
  chords: { at: number; chord: string }[]
}

const flagsOf = (segment: ChartSegment): Flags => ({
  ...(segment.bold ? { bold: true as const } : {}),
  ...(segment.italic ? { italic: true as const } : {}),
  ...(segment.underline ? { underline: true as const } : {}),
  ...(segment.note ? { note: true as const } : {}),
  ...(segment.chartOnly ? { chartOnly: true as const } : {}),
})

/** A lyrics line's segments as a draft to edit. */
export function lineDraft(segments: readonly ChartSegment[]): LineDraft {
  const draft: LineDraft = { text: '', flags: [], chords: [] }
  for (const segment of segments) {
    if (segment.chord) draft.chords.push({ at: draft.text.length, chord: segment.chord })
    const flags = flagsOf(segment)
    for (const char of segment.lyric) {
      draft.text += char
      draft.flags.push(flags)
    }
  }
  return draft
}

const STYLES = [
  ['bold', 'b'],
  ['italic', 'i'],
  ['underline', 'u'],
] as const

/**
 * A draft written as one ChordPro line: `[C]` before the letter a chord
 * falls on, `<b>`/`<i>`/`<u>` around styled runs, `{ }`/`{{ }}` around notes.
 */
export function serializeDraft(draft: LineDraft): string {
  const chords = [...draft.chords].sort((a, b) => a.at - b.at)
  let out = ''
  let open: Flags = {}
  let next = 0
  const closeAll = () => {
    if (open.note) out += open.chartOnly ? '}}' : '}'
    for (const [field, tag] of [...STYLES].reverse()) if (open[field]) out += `</${tag}>`
    open = {}
  }
  for (let i = 0; i <= draft.text.length; i += 1) {
    const flags = i < draft.text.length ? (draft.flags[i] ?? {}) : {}
    const changes =
      STYLES.some(([field]) => Boolean(flags[field]) !== Boolean(open[field])) ||
      Boolean(flags.note) !== Boolean(open.note) ||
      Boolean(flags.chartOnly) !== Boolean(open.chartOnly)
    if (changes) closeAll()
    while (next < chords.length && (chords[next]?.at ?? 0) <= i) {
      out += `[${chords[next]?.chord ?? ''}]`
      next += 1
    }
    if (i === draft.text.length) break
    if (changes) {
      for (const [field, tag] of STYLES) if (flags[field]) out += `<${tag}>`
      if (flags.note) out += flags.chartOnly ? '{{' : '{'
      open = flags
    }
    out += draft.text[i] ?? ''
  }
  closeAll()
  return out.replace(/\s+$/, '')
}

const tokensText = (tokens: readonly ChartToken[]) =>
  tokens.map((token) => ('chord' in token ? token.chord : token.text)).join('')

/** The text, its braces taken out: a note cannot close itself early. */
const noteText = (text: string) => text.replace(/[{}]/g, '').trim()

/** Whether a written line reads back as this one line. */
function readsAs(written: string, line: ChartLine): boolean {
  const lines = parseChart(written).lines
  return lines.length === 1 && JSON.stringify(lines[0]) === JSON.stringify(line)
}

/**
 * One chart line written out, in the format edited lines take: ChordPro
 * inline chords, plain headings (or `[Label]` when a plain one would read
 * as a lyric), Planning Center codes. Reads back as the same line.
 */
export function serializeLine(line: ChartLine): string {
  switch (line.kind) {
    case 'blank':
      return ''
    case 'columnBreak':
      return 'COLUMN_BREAK'
    case 'pageBreak':
      return line.chartOnly ? '{{ PAGE_BREAK }}' : 'PAGE_BREAK'
    case 'key':
      return `${line.mode === 'redefine' ? 'REDEFINE' : 'TRANSPOSE'} KEY ${line.semitones >= 0 ? '+' : ''}${line.semitones}`
    case 'comment':
      return line.chartOnly ? `{{ ${noteText(line.text)} }}` : `{comment: ${noteText(line.text)}}`
    case 'progression':
      return tokensText(line.tokens).trim()
    case 'section': {
      const label = line.label.replace(/[[\]{}]/g, '').trim()
      const plain = line.label === label ? label : null
      const tail = line.tokens.length > 0 ? ` ${tokensText(line.tokens).trim()}` : ''
      if (plain !== null && readsAs(`${plain}${tail}`, line)) return `${plain}${tail}`
      // A heading a plain line would not read as ("Big Ending"): bracketed, its progression under it.
      const bracketed = `[${label}]`
      if (tail === '' && readsAs(bracketed, line)) return bracketed
      const heading = readsAs(bracketed, { kind: 'section', label, tokens: [] })
        ? bracketed
        : `{start_of_part: ${label}}`
      return tail ? `${heading}\n${tail.trim()}` : heading
    }
    case 'lyrics':
      return serializeDraft(lineDraft(line.segments))
  }
}

/** A draft back as the line it reads as (what the screen shows once it is saved). */
export function draftLine(draft: LineDraft): ChartLine {
  const written = serializeDraft(draft)
  return parseChart(written).lines[0] ?? { kind: 'blank' }
}

// ---- editing a draft --------------------------------------------------------------------

const clampAt = (draft: LineDraft, at: number) => Math.max(0, Math.min(draft.text.length, at))
const sorted = (chords: LineDraft['chords']) => [...chords].sort((a, b) => a.at - b.at)

/** A chord placed on a letter (replacing the one already there). */
export function placeChord(draft: LineDraft, at: number, chord: string): LineDraft {
  const where = clampAt(draft, at)
  const name = chord.trim()
  if (!name) return draft
  return {
    ...draft,
    chords: sorted([...draft.chords.filter((c) => c.at !== where), { at: where, chord: name }]),
  }
}

/** The chord at `index` (in order) moved to another letter. */
export function moveChord(draft: LineDraft, index: number, to: number): LineDraft {
  const chords = sorted(draft.chords)
  const moving = chords[index]
  if (!moving) return draft
  const where = clampAt(draft, to)
  return {
    ...draft,
    chords: sorted([
      ...chords.filter((c, i) => i !== index && c.at !== where),
      { ...moving, at: where },
    ]),
  }
}

/** The chord at `index` (in order) renamed. */
export function renameChord(draft: LineDraft, index: number, chord: string): LineDraft {
  const chords = sorted(draft.chords)
  const name = chord.trim()
  if (!chords[index] || !name) return draft
  return { ...draft, chords: chords.map((c, i) => (i === index ? { ...c, chord: name } : c)) }
}

export function removeChord(draft: LineDraft, index: number): LineDraft {
  return { ...draft, chords: sorted(draft.chords).filter((_, i) => i !== index) }
}

/**
 * The words changed, the chords kept on the letters they were on: what is
 * typed before a chord pushes it along, a chord over letters taken out moves
 * to where they were. New letters take the styling beside them.
 */
export function editDraftText(draft: LineDraft, text: string): LineDraft {
  const old = draft.text
  let prefix = 0
  while (prefix < old.length && prefix < text.length && old[prefix] === text[prefix]) prefix += 1
  let suffix = 0
  while (
    suffix < old.length - prefix &&
    suffix < text.length - prefix &&
    old[old.length - 1 - suffix] === text[text.length - 1 - suffix]
  )
    suffix += 1
  const oldEnd = old.length - suffix
  const newEnd = text.length - suffix
  const delta = newEnd - oldEnd
  const near = draft.flags[prefix - 1] ?? draft.flags[prefix] ?? {}
  const flags = [
    ...draft.flags.slice(0, prefix),
    ...Array.from({ length: newEnd - prefix }, () => near),
    ...draft.flags.slice(oldEnd),
  ]
  const chords = draft.chords.map((c) => ({
    ...c,
    at: c.at < prefix ? c.at : c.at >= oldEnd ? c.at + delta : prefix,
  }))
  // Two chords landing on one letter: the later one stays.
  const byAt = new Map<number, { at: number; chord: string }>()
  for (const c of sorted(chords))
    byAt.set(Math.min(c.at, text.length), { ...c, at: Math.min(c.at, text.length) })
  return { text, flags, chords: [...byAt.values()] }
}

/** Where each word of a line starts: the places a tap puts a chord. */
export function wordStarts(text: string): number[] {
  const starts: number[] = []
  const pattern = /\S+/g
  for (let match = pattern.exec(text); match; match = pattern.exec(text)) starts.push(match.index)
  return starts
}

// ---- the chord palette ------------------------------------------------------------------

const MAJOR_PALETTE = {
  diatonic: ['C', 'Dm', 'Em', 'F', 'G', 'Am', 'B°'],
  more: ['C/E', 'F/A', 'G/B', 'Csus4', 'Gsus4', 'D', 'E', 'Bb', 'Fm', 'Ab', 'C7', 'G7'],
}
const MINOR_PALETTE = {
  diatonic: ['Am', 'B°', 'C', 'Dm', 'Em', 'F', 'G'],
  more: ['E', 'E7', 'Am/C', 'C/G', 'D', 'Esus4', 'G/B', 'Bb', 'A', 'Dm7', 'F/A', 'Am7'],
}

/**
 * The chords to offer for a key: its seven diatonic chords first, then the
 * common borrowed and slash chords. With no key, C major's.
 */
export function chordPalette(key: Key | string | null): { diatonic: string[]; more: string[] } {
  const target = typeof key === 'string' ? parseKey(key) : key
  const minor = target?.minor ?? false
  const palette = minor ? MINOR_PALETTE : MAJOR_PALETTE
  const from = parseKey(minor ? 'Am' : 'C')
  if (!target || !from) return { diatonic: [...palette.diatonic], more: [...palette.more] }
  const move = (chord: string) => transposeChord(chord, from, target)
  return { diatonic: palette.diatonic.map(move), more: palette.more.map(move) }
}

/** Common section names to start a new section with. */
export const SECTION_PRESETS = [
  'Intro',
  'Verse 1',
  'Verse 2',
  'Verse 3',
  'Pre-Chorus',
  'Chorus',
  'Bridge',
  'Tag',
  'Interlude',
  'Instrumental',
  'Outro',
  'Ending',
] as const
